import { Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ListTodo } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/queryClient";
import { toast } from "sonner";
import { QuickAddTask } from "./QuickAddTask";
import { TaskLinkChips } from "@/components/tasks/TaskLinkChips";
import type { TaskLink } from "@shared/taskLinks";

interface Task {
  id: string;
  title: string;
  status: "pending" | "in_progress" | "completed" | "cancelled";
  dueDate?: string | null;
  links?: TaskLink[];
}

function toDateOnly(d: Date) {
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}
function parseDueDate(s: string): Date {
  const dateStr = s.includes("T") ? s.split("T")[0] : s;
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
}
type Bucket = "overdue" | "today" | "later" | "no_date";
function bucketOf(dueDate: string | null | undefined): Bucket {
  if (!dueDate) return "no_date";
  const today = toDateOnly(new Date());
  const due = toDateOnly(parseDueDate(dueDate));
  if (due < today) return "overdue";
  if (due === today) return "today";
  return "later";
}

const MAX_VISIBLE = 8;

function TaskRow({ task }: { task: Task }) {
  const qc = useQueryClient();
  const complete = useMutation({
    mutationFn: () => apiRequest("POST", `/api/tasks/${task.id}/complete`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/tasks"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "radar"] });
      toast.success("Task completed");
    },
    onError: () => toast.error("Couldn't complete task"),
  });
  return (
    <div className="flex items-start gap-2 py-1.5 px-1 rounded hover:bg-accent transition-colors">
      <button
        onClick={() => complete.mutate()}
        disabled={complete.isPending}
        className="mt-0.5 flex-shrink-0 rounded-full p-0.5 hover:bg-primary/10 text-muted-foreground hover:text-primary-text"
        aria-label="Complete task"
      >
        <CheckCircle2 className="h-4 w-4" />
      </button>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-foreground truncate">{task.title}</p>
        <TaskLinkChips links={task.links ?? []} max={1} className="mt-0.5 flex-nowrap" />
      </div>
    </div>
  );
}

function Group({ label, tasks }: { label: string; tasks: Task[] }) {
  if (tasks.length === 0) return null;
  return (
    <section>
      <h4 className="px-1 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label} <span className="tabular-nums text-muted-foreground/60">({tasks.length})</span>
      </h4>
      <div>
        {tasks.map((t) => (
          <TaskRow key={t.id} task={t} />
        ))}
      </div>
    </section>
  );
}

export function TasksRail() {
  const { data: tasks = [], isLoading, isError, refetch } = useQuery<Task[]>({
    queryKey: ["/api/tasks"],
  });

  if (isLoading) {
    return (
      <div className="space-y-2 p-3">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-8 w-full rounded" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground" role="alert">
        Couldn't load tasks.{" "}
        <button className="underline" onClick={() => refetch()}>
          Retry
        </button>
      </div>
    );
  }

  const active = tasks.filter((t) => t.status === "pending" || t.status === "in_progress");
  const overdue = active.filter((t) => bucketOf(t.dueDate) === "overdue");
  const today = active.filter((t) => bucketOf(t.dueDate) === "today");
  const visibleCount = overdue.length + today.length;

  let oCapped = overdue;
  let tCapped = today;
  let trimmed = 0;
  if (visibleCount > MAX_VISIBLE) {
    if (overdue.length >= MAX_VISIBLE) {
      oCapped = overdue.slice(0, MAX_VISIBLE);
      tCapped = [];
    } else {
      oCapped = overdue;
      tCapped = today.slice(0, MAX_VISIBLE - overdue.length);
    }
    trimmed = visibleCount - MAX_VISIBLE;
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto p-3">
      <div className="flex items-center justify-between px-1">
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
          <ListTodo className="inline h-3 w-3 mr-1" /> Tasks ({visibleCount})
        </h3>
        <Link href="/tasks" className="text-xs text-muted-foreground hover:text-foreground underline">
          View all
        </Link>
      </div>
      <QuickAddTask />
      {visibleCount === 0 ? (
        <p className="text-sm text-muted-foreground italic px-1 py-2">✓ Nothing on your plate.</p>
      ) : (
        <>
          <Group label="Overdue" tasks={oCapped} />
          <Group label="Today" tasks={tCapped} />
          {trimmed > 0 && (
            <Link
              href="/tasks"
              className="block text-center text-xs text-muted-foreground hover:text-foreground underline mt-2"
            >
              + {trimmed} more
            </Link>
          )}
        </>
      )}
    </div>
  );
}
